import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ResultEntry from '../components/ResultEntry';
import searchService from '../services/searchService';
import seoService from '../services/seoService';
import { resetHomeCache } from './Home';

const SITE_URL = 'https://buscatuauto.cl';
const PAGE_SIZE = 60;
const RED = '#C31617';

const PREFIX = { marca: '/autos/', ciudad: '/autos-usados/' };

const formatPrice = (n) => `$${Number(n).toLocaleString('es-CL')}`;

// Los N valores más repetidos de una lista (ej. modelos o marcas más publicados).
// Agrupa variantes de escritura ("Cx-5", "CX5") y muestra la más frecuente.
const topValues = (values, n) => {
  const groups = {};
  values.filter(Boolean).forEach(v => {
    const key = v.toLowerCase().replace(/[^a-z0-9]/g, '');
    const g = groups[key] || (groups[key] = { total: 0, names: {} });
    g.total++;
    g.names[v] = (g.names[v] || 0) + 1;
  });
  return Object.values(groups)
    .sort((a, b) => b.total - a.total)
    .slice(0, n)
    .map(g => Object.entries(g.names).sort((a, b) => b[1] - a[1])[0][0]);
};

// Texto introductorio generado desde los datos, distinto para cada página
const buildIntro = (type, name, total, results, complete) => {
  const years = results.map(r => parseInt(r.year)).filter(y => y > 1900);
  const prices = results.map(r => r.price).filter(p => p > 0);
  const parts = [];

  if (type === 'marca') {
    parts.push(`Hay ${total} ${name} usados a la venta en BuscaTuAuto`);
  } else {
    parts.push(`Hay ${total} autos usados a la venta en ${name}`);
  }
  if (complete && years.length) {
    const min = Math.min(...years), max = Math.max(...years);
    parts.push(min === max ? `del año ${min}` : `de los años ${min} a ${max}`);
  }
  if (complete && prices.length) parts.push(`con precios desde ${formatPrice(Math.min(...prices))}`);

  let text = parts.join(', ') + '.';
  if (type === 'marca') {
    const models = topValues(results.map(r => r.model), 4);
    if (models.length > 1) text += ` Los modelos con más avisos son ${models.slice(0, -1).join(', ')} y ${models[models.length - 1]}.`;
  } else {
    const brands = topValues(results.map(r => r.brand?.name), 4);
    if (brands.length > 1) text += ` Las marcas con más avisos son ${brands.slice(0, -1).join(', ')} y ${brands[brands.length - 1]}.`;
  }
  return text + ' Reunimos en un solo lugar avisos de automotoras y particulares de todo Chile.';
};

export default function LandingPage({ type }) {
  const { slug } = useParams();
  const [landings, setLandings] = useState(null);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [isLast, setIsLast] = useState(true);
  const [loading, setLoading] = useState(true);

  const landing = landings?.find(l => l.type === type && l.slug === slug);
  const filterKey = type === 'marca' ? 'maker' : 'city';

  useEffect(() => {
    seoService.getLandings().then(setLandings).catch(() => setLandings([]));
  }, []);

  useEffect(() => {
    if (!landings) return;
    if (!landing) { setLoading(false); return; }
    setLoading(true);
    searchService.searchVehicles({ [filterKey]: landing.name, page: 0, size: PAGE_SIZE })
      .then(res => {
        setResults(res.content || []);
        setTotal(res.totalElements || 0);
        setPage(0);
        setIsLast(res.last !== false);
      })
      .catch(() => { setResults([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [landings, landing?.name, filterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = async () => {
    setLoading(true);
    try {
      const res = await searchService.searchVehicles({ [filterKey]: landing.name, page: page + 1, size: PAGE_SIZE });
      setResults(prev => [...prev, ...(res.content || [])]);
      setPage(page + 1);
      setIsLast(res.last !== false);
    } finally {
      setLoading(false);
    }
  };

  const canonical = `${SITE_URL}${PREFIX[type]}${slug}`;

  if (landings && !landing) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '40px 20px', textAlign: 'center' }}>
        <Helmet>
          <title>Página no encontrada | BuscaTuAuto</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>No encontramos autos para esta búsqueda</h1>
        <p style={{ color: '#777' }}>Puede que ya no haya avisos publicados.</p>
        <Link to="/" style={{ color: RED, fontWeight: 700 }}>Ver todos los autos</Link>
      </div>
    );
  }

  const name = landing?.name || '';
  const h1 = type === 'marca' ? `${name} usados en venta` : `Autos usados en ${name}`;
  const metaTitle = type === 'marca'
    ? `${name} usados en venta en Chile | BuscaTuAuto`
    : `Autos usados en ${name} | BuscaTuAuto`;
  const complete = isLast && results.length >= total;
  const intro = landing && results.length ? buildIntro(type, name, total, results, complete) : '';
  const metaDesc = intro || metaTitle;

  const related = (landings || []).filter(l => l.indexable && !(l.type === type && l.slug === slug));
  const relatedBrands = related.filter(l => l.type === 'marca').slice(0, 20);
  const relatedCities = related.filter(l => l.type === 'ciudad').slice(0, 10);

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '20px 20px 60px' }}>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonical} />
        {landing && !landing.indexable && <meta name="robots" content="noindex, follow" />}
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
      </Helmet>

      <nav style={{ fontSize: 13, color: '#888', marginBottom: 10 }}>
        <Link to="/" style={{ color: '#888' }}>Inicio</Link>
        {' › '}
        <span>{type === 'marca' ? 'Marcas' : 'Ciudades'}</span>
        {' › '}
        <span>{name}</span>
      </nav>

      {landing && <h1 style={{ fontSize: 26, fontWeight: 900, color: '#111', margin: '0 0 10px' }}>{h1}</h1>}
      {intro && <p style={{ fontSize: 15, color: '#555', lineHeight: 1.6, marginBottom: 16 }}>{intro}</p>}

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <Link
          to={`/?${filterKey}=${encodeURIComponent(name)}`}
          onClick={resetHomeCache}
          style={{ background: RED, color: '#fff', padding: '8px 16px', borderRadius: 20, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
        >
          Filtrar por año, precio y más
        </Link>
        <Link
          to="/publicar"
          style={{ border: `2px solid ${RED}`, color: RED, padding: '6px 16px', borderRadius: 20, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}
        >
          {type === 'marca' ? `¿Vendes tu ${name}? Publícalo gratis` : 'Publica tu auto gratis'}
        </Link>
      </div>

      <div className="container-home-feed">
        {results.map((item, i) => (
          <section key={item.id}>
            <br />
            <ResultEntry entry={item} />
            {i < results.length - 1 && <hr className="d-none d-md-block" />}
          </section>
        ))}
      </div>

      {loading && <p style={{ textAlign: 'center', padding: 20 }}>Cargando vehículos...</p>}
      {!loading && !isLast && (
        <div style={{ textAlign: 'center', padding: 20 }}>
          <button onClick={loadMore} style={{ background: 'none', border: `2px solid ${RED}`, color: RED, borderRadius: 20, padding: '6px 20px', fontWeight: 700 }}>
            Ver más autos
          </button>
        </div>
      )}

      {(relatedBrands.length > 0 || relatedCities.length > 0) && (
        <div style={{ marginTop: 40, borderTop: '1px solid #eee', paddingTop: 20 }}>
          {relatedBrands.length > 0 && (
            <>
              <h2 style={{ fontSize: 17, fontWeight: 800 }}>Autos usados por marca</h2>
              <LinkList items={relatedBrands} prefix={PREFIX.marca} />
            </>
          )}
          {relatedCities.length > 0 && (
            <>
              <h2 style={{ fontSize: 17, fontWeight: 800, marginTop: 20 }}>Autos usados por ciudad</h2>
              <LinkList items={relatedCities} prefix={PREFIX.ciudad} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function LinkList({ items, prefix }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: 14 }}>
      {items.map(l => (
        <Link key={l.slug} to={`${prefix}${l.slug}`} style={{ color: '#444' }}>
          {l.name} ({l.count})
        </Link>
      ))}
    </div>
  );
}
